import type { ReactNode } from 'react'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

import Layout from '../../components/layout/Layout'
import { Badge, Progress, Spinner } from '../../components/ui'
import { useLagAccrualRun } from './api'
import { balanceLabel, formatCurrency } from './utils'

export default function LagAccrualsLayout({ children }: { children: ReactNode }) {
  const runQuery = useLagAccrualRun()
  const run = runQuery.data

  return (
    <Layout>
      <div className="h-full overflow-auto bg-gray-50 dark:bg-dark-bg">
        <div className="mx-auto flex max-w-[1680px] flex-col gap-6 px-6 py-6">
          <header className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
            <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_420px]">
              <div className="flex flex-col justify-center gap-3 p-6 sm:p-7">
                <div className="max-w-3xl">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <Badge variant="default">EC-02d</Badge>
                    <Badge variant="secondary">Day -2 close workflow</Badge>
                    {run?.hitl_count ? (
                      <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        {run.hitl_count} HITL required
                      </Badge>
                    ) : null}
                    {run ? (
                      <Badge variant="secondary">
                        {run.data_source.mode === 'redshift'
                          ? `Redshift: ${run.data_source.redshift_schema}`
                          : 'Fixture data'}
                      </Badge>
                    ) : null}
                  </div>
                  <h1 className="font-title text-3xl text-charcoal dark:text-gray-100">
                    Variable & Lag Accruals
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                    Controller workbench for activity-driven accruals: T&E lag, open legal matters,
                    unposted office events, and marketing programs in flight.
                  </p>
                </div>
              </div>

              <aside className="border-t border-gray-200 bg-gray-50 p-6 dark:border-dark-border dark:bg-dark-surface-2 lg:border-l lg:border-t-0">
                {runQuery.isLoading ? (
                  <div className="flex h-full min-h-[140px] items-center justify-center">
                    <Spinner size="lg" />
                  </div>
                ) : run ? (
                  <div className="flex h-full flex-col justify-between gap-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Period
                        </p>
                        <p className="mt-1 text-lg font-semibold text-charcoal dark:text-gray-100">
                          {run.period}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Entity
                        </p>
                        <p className="mt-1 truncate text-lg font-semibold text-charcoal dark:text-gray-100">
                          {run.entity.replace(', Inc.', '')}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-end justify-between gap-4">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            Accrual total
                          </p>
                          <p className="mt-1 text-3xl font-semibold text-charcoal dark:text-gray-100">
                            {formatCurrency(run.grand_total)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-300">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          Over threshold
                        </div>
                      </div>
                      <Progress
                        value={Math.min((run.grand_total / run.total_threshold) * 100, 100)}
                        className="mt-4 h-2"
                      />
                    </div>

                    <div className="grid gap-2 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <DocumentArrowDownIcon className="h-4 w-4" />
                          JE load
                        </span>
                        <span className="font-medium text-charcoal dark:text-gray-100">
                          {balanceLabel(run.journal_balance)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <ArrowPathIcon className="h-4 w-4" />
                          Reversal
                        </span>
                        <span className="font-medium text-charcoal dark:text-gray-100">
                          {balanceLabel(run.reversal_balance)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <CheckCircleIcon className="h-4 w-4" />
                          Source files
                        </span>
                        <span className="font-medium text-charcoal dark:text-gray-100">
                          {run.source_files.length}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Unable to load lag accrual run.
                  </p>
                )}
              </aside>
            </div>
          </header>

          {children}
        </div>
      </div>
    </Layout>
  )
}
